import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, Auth, GoogleAuthProvider } from "firebase/auth";

/**
 * Config do Firebase por env (`VITE_FIREBASE_*`), preenchida em
 * `.env`/`.env.production`. Não são secrets — vão para o bundle do cliente por
 * design (a proteção real é a allowlist de domínios do Firebase Console + as
 * Firestore rules).
 *
 * `authDomain` é o apex do produto, NÃO o `*.firebaseapp.com` default: deixa o
 * fluxo de auth same-origin e elimina o `auth/missing-initial-state` de webview
 * (ver `lib/in-app-browser.ts`). O Hosting reserva `/__/*` acima dos rewrites.
 * Trocar por `${projectId}.firebaseapp.com` reintroduz o bug de webview.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Fail-fast: sem a chave, o SDK só quebraria no primeiro login com
// `auth/invalid-api-key` — longe da causa (uma env esquecida no build). Falhar
// aqui torna o build/boot o lugar onde o erro aparece.
if (!firebaseConfig.apiKey) {
  throw new Error(
    'VITE_FIREBASE_API_KEY ausente. Preencha o bloco VITE_FIREBASE_* ' +
      '(ver .env.example) no ambiente de build.'
  );
}

// Types nullable: só inicializados no browser (typeof window !== 'undefined').
// Em SSR (vite-react-ssg), permanecem undefined — callers devem usar optional chaining.
let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let googleProvider: GoogleAuthProvider | undefined;

// Guard SSR: Firebase Auth usa IndexedDB/localStorage que não existem no Node.
// Durante pre-renderização (vite-react-ssg), typeof window === 'undefined'.
if (typeof window !== 'undefined') {
  // Initialize Firebase
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }

  // Initialize Auth
  auth = getAuth(app);

  // Aponta o SDK para um Auth emulator quando a env existe. A AUSÊNCIA da env é
  // o comportamento de produção — mesmo padrão de VITE_SENTRY_DSN e
  // VITE_POSTHOG_KEY. Nenhum ambiente de produção define esta variável.
  const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
  if (emulatorHost) {
    connectAuthEmulator(auth, emulatorHost, { disableWarnings: true });
  }

  // Idioma dos e-mails de auth (verificação, reset de senha) e do widget OAuth.
  // Sem isto o Firebase usa o default 'en' (e-mail saía em inglês, lang=en).
  auth.languageCode = 'pt-BR';

  // Google Auth Provider
  googleProvider = new GoogleAuthProvider();
  // Sempre mostrar o seletor de conta, mesmo se o usuário já fez login antes
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });
}

export { auth, googleProvider };
export default app;
